import type { PatientResponse } from '@/services/patients';
import type { IncomingStudy, IncomingStudyReport } from '@/services/incomingStudies';
import type { UserSiteAccessItem } from '@/services/userSites';
import { getFileUrl } from '@/utils/fileUrl';
import { formatFullName, formatPersonName } from '@/utils/personName';
import { resolvePatientAgeYears } from '@/utils/patientAge';
import { institutionDisplayName } from '@/utils/siteDisplayName';
import {
  accentFromKey,
  formatDocumentDisplay,
  initialsFromName,
  normalizePatientIdForGrouping,
  personKeyFromDocument,
  rollupStatus,
  sitesSummaryLabel,
  statusLabelFor,
  type ReferrerClinicalNote,
  type ReferrerPatientSex,
  type ReferrerPatientStatus,
  type ReferrerPatientStudy,
  type ReferrerPersonCard,
  type ReferrerProfileSite,
  type ReferrerSiteRecord,
  type ReferrerStudyKind,
  type ReferrerStudyReport,
  type ReferrerStudyStep,
  type ReferrerStudyTone,
} from './referrerPatientTypes';
import { filterStudiesForReferrer } from './referrerStudyVisibility';

/** DICOM modalities treated as laboratory; OT/SR are imaging-related, not lab. */
const LAB_MODALITIES = new Set(['LAB']);

function modalityCodes(raw: string): string[] {
  const normalized = raw.trim().replace(/\\/g, ',').replace(/\//g, ',').replace(/\|/g, ',');
  return normalized
    .split(',')
    .map((p) => p.trim().toUpperCase())
    .filter(Boolean);
}

function studyKind(modality: string): ReferrerStudyKind {
  const codes = modalityCodes(modality);
  if (codes.length === 0) return 'imaging';
  if (codes.some((code) => LAB_MODALITIES.has(code))) return 'lab';
  return 'imaging';
}

const PIPELINE_LABELS = ['Referido', 'Confirmado', 'En proceso', 'Resultado'] as const;

export function sitesFromAccess(items: UserSiteAccessItem[]): ReferrerProfileSite[] {
  return items
    .filter((s) => s.status === 'granted')
    .map((s) => ({
      id: s.node_id,
      nodeId: s.node_id,
      label: institutionDisplayName(s, s.node_id),
      status: 'granted' as const,
      color: s.color,
      viewPatients: s.view_patients,
    }));
}

export function mapSex(raw?: string): ReferrerPatientSex {
  const s = (raw || '').trim().toUpperCase();
  if (s === 'F' || s === 'FEMALE' || s === 'FEMENINO') return 'F';
  if (s === 'M' || s === 'MALE' || s === 'MASCULINO') return 'M';
  if (s === 'O' || s === 'OTHER' || s === 'OTRO' || s === 'X') return 'O';
  // Empty / unknown legacy values: keep previous default (M), never coerce explicit O away.
  return 'M';
}

function resolvePdfUrl(report: IncomingStudyReport): string | undefined {
  // Prefer view URL over download URL so "Ver informe" opens inline, not as attachment.
  const path = report.pdf_url || report.url_author || report.pdf_download_url || '';
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return getFileUrl(path) || undefined;
}

function reportOrigin(report: IncomingStudyReport): 'native' | 'third_party' {
  const raw = (report.report_origin || '').trim().toLowerCase();
  if (raw === 'third_party' || (report.type || '').toLowerCase() === 'old-method-report') {
    return 'third_party';
  }
  return 'native';
}

function mapReport(report: IncomingStudyReport): ReferrerStudyReport {
  const statusRaw = (report.status || '').toLowerCase();
  const completed =
    statusRaw === 'completed' || statusRaw === 'signed' || statusRaw === 'firmado';
  const origin = reportOrigin(report);
  const when = (report.signed_at || report.created_at || '').trim() || undefined;
  return {
    id: report.id || report.report_client_id || crypto.randomUUID(),
    title: report.title || (origin === 'third_party' ? 'Informe externo' : 'Informe'),
    physicianName: report.physician_name || report.author_name || report.signed_by || '—',
    physicianSerial: report.physician_serial || report.serial_author || '—',
    status: completed ? 'completed' : 'draft',
    origin,
    // ISO crudo: la UI formatea con el idioma activo (ES 12h / EN 24h).
    signedAt: when,
    findings: report.summary || '',
    conclusion: completed ? report.summary || '' : '',
    pdfUrl: resolvePdfUrl(report),
  };
}

function stepsForProgress(progress: number, firstLabel = 'Referido'): ReferrerStudyStep[] {
  const labels = [firstLabel, ...PIPELINE_LABELS.slice(1)];
  return labels.map((label, idx) => {
    const stepNum = idx + 1;
    return {
      label,
      done: progress >= stepNum,
      current: progress === stepNum || (progress >= 4 && stepNum === 4),
    };
  });
}

function studyHasReferrer(study: IncomingStudy): boolean {
  const name = (study.referring_physician_name || '').trim();
  if (name) return true;
  return (study.assigned ?? []).some(
    (a) => (a.user_type || '').trim().toUpperCase() === 'REFERRER' && (a.serial || '').trim(),
  );
}

export function progressFromStudy(study: IncomingStudy): {
  progress: number;
  status: ReferrerPatientStatus;
  statusLabel: string;
  tone: ReferrerStudyTone;
} {
  const reports = study.reports ?? [];
  const hasReport = reports.length > 0;
  const hasCompletedReport = reports.some((r) => {
    const s = (r.status || '').toLowerCase();
    return s === 'completed' || s === 'signed' || s === 'firmado';
  });

  const wf = study.workflow_status ?? 1;

  if (wf >= 3 || hasCompletedReport) {
    return {
      progress: 4,
      status: 'ready',
      statusLabel: 'Reportado',
      tone: 'green',
    };
  }

  if (hasReport) {
    return {
      progress: 3,
      status: 'ready',
      statusLabel: 'Reportado',
      tone: 'green',
    };
  }

  const arrived = study.arrived_at ? new Date(study.arrived_at) : null;
  const ageDays =
    arrived && !Number.isNaN(arrived.getTime())
      ? (Date.now() - arrived.getTime()) / (1000 * 60 * 60 * 24)
      : 0;

  if (ageDays > 5) {
    return { progress: 2, status: 'overdue', statusLabel: 'Atrasado', tone: 'red' };
  }

  if (wf <= 1 && ageDays < 1) {
    // "Referido" only when there is an actual referring physician / REFERRER assignment.
    const referred = studyHasReferrer(study);
    return {
      progress: 1,
      status: 'new',
      statusLabel: referred ? 'Nuevo referido' : 'Nuevo',
      tone: 'blue',
    };
  }

  return { progress: 2, status: 'in_process', statusLabel: 'En proceso', tone: 'teal' };
}

export function formatRelativeTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return mins <= 1 ? 'Hace un momento' : `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours === 1 ? 'Hace 1 hora' : `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  return d.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function isRecent(iso?: string): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() < 3 * 24 * 60 * 60 * 1000;
}

function assignmentSerialsForType(study: IncomingStudy, userType: string): string[] {
  const want = userType.trim().toUpperCase();
  const out: string[] = [];
  for (const a of study.assigned ?? []) {
    if ((a.user_type || '').trim().toUpperCase() !== want) continue;
    const serial = (a.serial || '').trim();
    if (serial && !out.some((s) => s.toLowerCase() === serial.toLowerCase())) {
      out.push(serial);
    }
  }
  return out;
}

/** Display label for assigned radiologist(s) / who took the case. */
export function studyRadiologistLabel(study: IncomingStudy): string {
  const parts: string[] = [];
  const takenName = (study.taken_by_name || '').trim();
  const takenSerial = (study.taken_by_serial || '').trim();
  if (takenName) {
    parts.push(takenSerial ? `${takenName} (${takenSerial})` : takenName);
  } else if (takenSerial) {
    parts.push(takenSerial);
  }
  const typed = assignmentSerialsForType(study, 'RADIOLOGIST');
  const flat = (study.assigned_serials ?? [])
    .map((s) => s.trim())
    .filter(Boolean);
  for (const serial of [...typed, ...flat]) {
    if (takenSerial && serial.toLowerCase() === takenSerial.toLowerCase()) continue;
    if (parts.some((p) => p.toLowerCase().includes(serial.toLowerCase()))) continue;
    parts.push(serial);
  }
  return parts.length ? parts.join(', ') : '—';
}

/** Display label for referring physician + REFERRER assignments. */
export function studyReferrerLabel(study: IncomingStudy): string {
  const parts: string[] = [];
  const name = (study.referring_physician_name || '').trim().replace(/\^/g, ' ');
  if (name) parts.push(name);
  for (const serial of assignmentSerialsForType(study, 'REFERRER')) {
    if (parts.some((p) => p.toLowerCase().includes(serial.toLowerCase()))) continue;
    parts.push(serial);
  }
  return parts.length ? parts.join(', ') : '—';
}

export function mapIncomingStudyToReferrer(
  study: IncomingStudy,
  profileSites: ReferrerProfileSite[],
): ReferrerPatientStudy {
  const siteMatch =
    profileSites.find((s) => s.nodeId === study.node_id) ||
    profileSites.find((s) => s.nodeId.toLowerCase() === (study.node_id || '').toLowerCase()) ||
    profileSites.find((s) => s.label === study.site);
  const siteId = siteMatch?.id || study.node_id;
  const siteLabel = siteMatch?.label || study.site || study.node_id;
  const { progress, statusLabel, tone } = progressFromStudy(study);
  const kind = studyKind(study.modality || '');
  const reports = (study.reports ?? []).map(mapReport);
  const hasReferrer = studyHasReferrer(study);

  // OHIF URL is resolved on demand via GET .../viewers (not list snapshot).
  return {
    id: study.study_id,
    kind,
    site: siteLabel,
    siteId,
    name: study.description || study.modality || 'Estudio',
    orderCode: study.accession_number || study.study_id,
    modality: study.modality || '—',
    requestedAt: formatRelativeTime(study.arrived_at || study.study_date),
    progress,
    tone,
    statusLabel,
    steps: stepsForProgress(progress, hasReferrer ? 'Referido' : 'Llegado'),
    reports,
    arrivedAt: study.arrived_at,
    radiologistLabel: studyRadiologistLabel(study),
    referrerLabel: studyReferrerLabel(study),
  };
}

export function notesFromStudies(studies: IncomingStudy[], siteLabel: (nodeId: string) => string): ReferrerClinicalNote[] {
  const notes: ReferrerClinicalNote[] = [];
  for (const st of studies) {
    if (st.clinical_note?.trim()) {
      notes.push({
        id: `cn-${st.study_id}`,
        author: 'Nota clínica',
        role: 'Estudio',
        site: siteLabel(st.node_id),
        at: formatRelativeTime(st.arrived_at),
        body: st.clinical_note.trim(),
      });
    }
    for (const c of st.doctor_comments ?? []) {
      const body = (c.body || c.text || c.comment || '').trim();
      if (!body) continue;
      notes.push({
        id: c.id || `dc-${st.study_id}-${notes.length}`,
        author: c.author_name || 'Médico',
        role: 'Comentario',
        site: siteLabel(st.node_id),
        at: formatRelativeTime(c.created_at),
        body,
      });
    }
  }
  return notes;
}

/**
 * Aggregates node-scoped patients + visible studies into one card per document.
 * Only persons with ≥1 study where the referrer has typed REFERRER assignment.
 */
export function toPersonCards(
  patients: PatientResponse[],
  studies: IncomingStudy[],
  profileSites: ReferrerProfileSite[],
  referrerSerial: string,
): ReferrerPersonCard[] {
  const visibleStudies = filterStudiesForReferrer(studies, referrerSerial);
  const siteByNode = new Map(profileSites.map((s) => [s.nodeId, s]));
  const labelFor = (nodeId: string) =>
    siteByNode.get(nodeId)?.label || nodeId;

  type Acc = {
    personKey: string;
    patients: PatientResponse[];
    studies: IncomingStudy[];
  };

  const byKey = new Map<string, Acc>();

  // Index patients by normalized document for later enrichment (not for listing alone).
  const patientsByDoc = new Map<string, PatientResponse[]>();
  for (const p of patients) {
    const doc = normalizePatientIdForGrouping(p.document_number);
    if (!doc) continue;
    const list = patientsByDoc.get(doc) ?? [];
    list.push(p);
    patientsByDoc.set(doc, list);
  }

  for (const st of visibleStudies) {
    const pacs = normalizePatientIdForGrouping(st.patient_pacs_id || '');
    if (!pacs) continue;

    const matchedPatients = patientsByDoc.get(pacs) ?? [];
    const documentType = matchedPatients[0]?.document_type || 'CEDULA';
    const key = personKeyFromDocument(documentType, pacs);

    const acc = byKey.get(key) ?? { personKey: key, patients: [], studies: [] };
    if (acc.patients.length === 0 && matchedPatients.length) {
      acc.patients.push(...matchedPatients);
    }
    acc.studies.push(st);
    byKey.set(key, acc);
  }

  const cards: ReferrerPersonCard[] = [];

  for (const acc of byKey.values()) {
    // Referrer must be referred / have visibility on at least one study.
    if (acc.studies.length === 0) continue;

    const primary =
      [...acc.patients].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      )[0] || null;

    const nameFromPatient = primary
      ? formatFullName(primary.first_name, primary.last_name)
      : '';
    const nameFromStudy = acc.studies[0]
      ? formatPersonName(acc.studies[0].patient_name.replace(/\^/g, ' '))
      : '';
    const name = nameFromPatient || nameFromStudy || 'Paciente';

    const documentType = primary?.document_type || 'CEDULA';
    const documentNumber =
      (primary && normalizePatientIdForGrouping(primary.document_number)) ||
      normalizePatientIdForGrouping(acc.studies[0]?.patient_pacs_id || '') ||
      '';
    const personKey = personKeyFromDocument(documentType, documentNumber);

    const mappedStudies = acc.studies.map((s) => mapIncomingStudyToReferrer(s, profileSites));

    const recordsBySite: ReferrerSiteRecord[] = [];
    for (const p of acc.patients) {
      const site = siteByNode.get(p.node_id);
      const siteId = site?.id || p.node_id;
      // Only show HC for sites where this referrer has assignments.
      if (!mappedStudies.some((s) => s.siteId === siteId)) continue;
      const siteName = site?.label || p.node_id;
      const hc = p.medical_record || p.id_node || `ID-${p.id}`;
      if (!recordsBySite.some((r) => r.siteId === siteId)) {
        recordsBySite.push({ siteId, siteName, hc });
      }
    }
    // Fallback site rows when no patient master row for that node.
    for (const s of mappedStudies) {
      if (recordsBySite.some((r) => r.siteId === s.siteId)) continue;
      recordsBySite.push({
        siteId: s.siteId,
        siteName: s.site,
        hc: '—',
      });
    }

    const siteIdsActive = [...new Set(mappedStudies.map((s) => s.siteId))];

    const studyStatuses = mappedStudies.map((s) => {
      if (s.progress >= 4) return 'ready' as ReferrerPatientStatus;
      if (s.tone === 'red') return 'overdue' as ReferrerPatientStatus;
      if (s.progress <= 1) return 'new' as ReferrerPatientStatus;
      return 'in_process' as ReferrerPatientStatus;
    });
    const status = rollupStatus(studyStatuses);

    const sortedByArrival = [...acc.studies].sort((a, b) => {
      const ta = new Date(a.arrived_at || a.study_date || 0).getTime();
      const tb = new Date(b.arrived_at || b.study_date || 0).getTime();
      return tb - ta;
    });
    const last = sortedByArrival[0];
    const lastMapped = last ? mapIncomingStudyToReferrer(last, profileSites) : null;

    const photo =
      (primary?.photo_url && getFileUrl(primary.photo_url)) ||
      (acc.studies.find((s) => s.patient_photo_url)?.patient_photo_url
        ? getFileUrl(acc.studies.find((s) => s.patient_photo_url)!.patient_photo_url!)
        : '') ||
      '';

    const activeLabels = siteIdsActive.map(
      (id) => profileSites.find((s) => s.id === id)?.label || id,
    );
    const siteChips = siteIdsActive.map((id) => {
      const site = profileSites.find((s) => s.id === id);
      return {
        siteId: id,
        label: site?.label || id,
        color: site?.color,
      };
    });

    cards.push({
      personKey,
      name,
      documentType,
      documentNumber,
      documentNumberDisplay: formatDocumentDisplay(
        primary?.document_number || documentNumber,
      ),
      sex: mapSex(primary?.sex || acc.studies[0]?.patient_sex),
      age: resolvePatientAgeYears(primary?.age || acc.studies[0]?.patient_age, primary?.birth_date),
      phone: primary?.phone || '',
      photoUrl: photo,
      initials: initialsFromName(name),
      accent: accentFromKey(personKey),
      recordsBySite,
      siteIdsActive,
      assignmentCount: mappedStudies.length,
      sitesActive: new Set(mappedStudies.map((s) => s.siteId)).size,
      sitesRegistered: profileSites.filter((s) => s.status === 'granted').length,
      primaryHc: recordsBySite[0]?.hc || '—',
      sitesSummary: sitesSummaryLabel(activeLabels),
      siteChips,
      lastStudy: lastMapped?.name || 'Sin estudios',
      lastVisit: lastMapped?.requestedAt || formatRelativeTime(primary?.updated_at),
      lastActivityAt: last?.arrived_at || last?.study_date || primary?.updated_at || undefined,
      status,
      statusLabel: statusLabelFor(status),
      starred: false,
      recent: isRecent(last?.arrived_at || primary?.updated_at),
      studies: mappedStudies,
      notes: notesFromStudies(acc.studies, labelFor),
    });
  }

  cards.sort((a, b) => {
    // Recent / overdue first-ish by last visit string is weak; prefer assignment count then name
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (b.status === 'overdue' && a.status !== 'overdue') return 1;
    return a.name.localeCompare(b.name, 'es');
  });

  return cards;
}
